import {
  PropsWithChildren,
  ReactNode,
  type TransitionEvent,
  useLayoutEffect,
  useMemo,
  useState
} from 'react'
import { FieldValues, FormProvider, UseFormReturn } from 'react-hook-form'
import Button from '../../Button'
import { Checkbox } from '../../form'
import { cn } from '../../../utils'
import { useTranslation } from 'react-i18next'
import ErrorAlert from '../../ErrorAlert'
import { BefehlFormConfigProvider } from './BefehlFormConfigContext'
import { useBefehlIcon } from '../../../icons/befehle'
import Heading from '../../Heading'

export interface BefehlFormWrapperProps<
  TFormData extends FieldValues
> extends PropsWithChildren {
  befehlKennung: string
  title: ReactNode
  formMethods: UseFormReturn<TFormData>
  onSubmit: (data: TFormData) => void
  'data-testid': string
  readOnly?: boolean
  activateEmptyFieldWarnings?: boolean
  displayHeaderOnly?: boolean
}

/**
 * Generic form wrapper component for Befehle, providing the form with a uniform header and footer.
 * @template TFormData The type of form data must be specified when using this component
 */
const BefehlFormWrapperComponent = <TFormData extends FieldValues>({
  befehlKennung,
  title,
  formMethods,
  onSubmit,
  children,
  'data-testid': testId,
  readOnly = false,
  activateEmptyFieldWarnings = false,
  displayHeaderOnly = false
}: Readonly<BefehlFormWrapperProps<TFormData>>) => {
  const { t } = useTranslation('ui-befehle')
  const { t: tCommon } = useTranslation('ui-common')
  const titleString = useMemo(() => {
    return typeof title === 'string' ? title : `Befehl ${befehlKennung}`
  }, [title, befehlKennung])

  // Global errors are in the empty string group of errors.
  // The path 'root' is not supported by the zod resolver yet:
  // https://github.com/react-hook-form/resolvers/issues/561
  const rootError = formMethods.formState.errors[''] as
    | { message?: string }
    | undefined
  const BefehlIcon = useBefehlIcon(befehlKennung)

  const [isFullyOpen, setIsFullyOpen] = useState(!displayHeaderOnly)

  useLayoutEffect(() => {
    if (displayHeaderOnly) setIsFullyOpen(false)
  }, [displayHeaderOnly])

  const handleTransitionEnd = (e: TransitionEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return
    if (!displayHeaderOnly) setIsFullyOpen(true)
  }

  return (
    <BefehlFormConfigProvider
      readOnly={readOnly}
      activateEmptyFieldWarnings={activateEmptyFieldWarnings}
    >
      <FormProvider {...formMethods}>
        <form
          onSubmit={formMethods.handleSubmit(onSubmit)}
          data-testid={testId}
          className="flex flex-col gap-1"
          data-empty-fields={
            activateEmptyFieldWarnings ? 'enabled' : 'disabled'
          }
          aria-readonly={readOnly}
        >
          {/* Header */}
          <div className="flex items-center relative">
            <div className="w-1.75 shrink-0">
              <Heading
                tag="h3"
                variant="span"
                className=" text-lg md:text-xl my-0 flex-grow font-bold text-right block font-head font-black"
                data-testid={`${testId}-befehl-kennung`}
              >
                {befehlKennung}
              </Heading>
            </div>
            <div className="w-1.75 shrink-0 flex justify-center items-center ml-0.5 mr-1">
              <Checkbox
                label={titleString}
                checked={true}
                onChange={() => {}}
                readOnly={true}
                hideLabel={true}
                data-testid={`${testId}-befehl-${befehlKennung}-checkbox`}
                aria-label={`${titleString} ${tCommon('form.isSelected')}`}
                role="status"
              />
            </div>
            <Heading
              tag="h3"
              className="my-0 flex-grow"
              data-testid={`${testId}-title`}
            >
              {title}
            </Heading>
            <div className="w-4 h-4">
              <BefehlIcon
                height={64}
                width={64}
                data-testid={`${testId}-illustration`}
              />
            </div>

            {displayHeaderOnly && (
              <div
                className="absolute inset-0 bg-white/50 dark:bg-cool-gray-800/65 pointer-events-none"
                data-testid={`${testId}-gray-out-overlay`}
              />
            )}
          </div>

          {/* Root Error Display */}
          {rootError && (
            <ErrorAlert
              className="mb-1"
              data-testid={`${testId}-validation-error`}
            >
              {rootError.message}
            </ErrorAlert>
          )}

          {/* Form Fields Container */}
          <div
            className={cn('grid transition-all duration-500 ease-in-out', {
              'grid-rows-[1fr] opacity-100': !displayHeaderOnly,
              'grid-rows-[0fr] opacity-0': displayHeaderOnly
            })}
            onTransitionEnd={handleTransitionEnd}
          >
            <div
              className={isFullyOpen ? 'overflow-visible' : 'overflow-hidden'}
            >
              <div
                className="flex flex-col relative [&>*+*]:border-t [&>*+*]:border-cool-gray-200 [&>*+*]:pt-1.5 [&>*:not(:last-child)]:pb-1.5 pt-[3px] pr-[3px]"
                data-testid={`${testId}-container`}
              >
                {children}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          {!readOnly && (
            <Button
              type="submit"
              variant="brand-primary"
              className="mt-1"
              data-testid={`${testId}-submit-button`}
            >
              <span data-testid={`${testId}-submit-button-text`}>
                {t('edit_next_button')}
              </span>
            </Button>
          )}
        </form>
      </FormProvider>
    </BefehlFormConfigProvider>
  )
}

export default BefehlFormWrapperComponent
